/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {Box, Container} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    AuthHelpers,
    useAuthHelper,
    useCustomerType,
    useShopperCustomersMutation
} from '@salesforce/commerce-sdk-react'
import {useForm} from 'react-hook-form'
import {useLocation} from 'react-router-dom'
import Seo from '@salesforce/retail-react-app/app/components/seo'
import RegisterForm from '../../components/register'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import {registerModalContent} from './index.style'
import {useLoginContext} from '../../components/login/loginContext'
import {pushAnalyticsEvent} from '../../../../overrides//app/utills/analytics'

const Registration = () => {
    const {formatMessage} = useIntl()
    const navigate = useNavigation()
    const {isRegistered} = useCustomerType()
    const form = useForm()
    const einstein = useEinstein()
    const {pathname, search} = useLocation()
    const [registrationSuccess, setRegistrationSuccess] = useState(false)
    const registerCustomerMutation = useShopperCustomersMutation('registerCustomer')
    const login = useAuthHelper(AuthHelpers.LoginRegisteredUserB2C)
    const {email} = useLoginContext()
    const [dlQueryParams, setDLQueryParams] = useState({})
    const searchParams = new URLSearchParams(search)

    const submitForm = async (data) => {
        const body = {
            customer: {
                firstName: data.firstName,
                lastName: data.lastName,
                email: data.email,
                login: data.email,
                phoneMobile: data.mobile,
                ...(data.birthday && data.birthday !== '' && {birthday: data.birthday}),
                ...(dlQueryParams.storeID &&
                    dlQueryParams.storeID !== '' && {c_storeID: dlQueryParams.storeID}),
                ...(dlQueryParams.cashier &&
                    dlQueryParams.cashier !== '' && {c_cashier: dlQueryParams.cashier}),
                c_registrationUrl: window.location.href
            },
            password: data.password
        }

        try {
            // submit form
            const userData = await registerCustomerMutation.mutateAsync(
                {
                    body,
                    parameters: {
                        c_isSkipRecaptcha: false,
                        c_grecaptcharesponse: data?.recaptcha
                    }
                },
                {
                    onSuccess: async () => {
                        await login.mutateAsync({
                            username: data.email,
                            password: data.password
                        })
                        if (typeof window !== 'undefined') {
                            localStorage.removeItem('passwordResetEmail')
                        }
                        if (typeof window !== 'undefined' && window.dataLayer) {
                            delete dlQueryParams.cashier
                            await pushAnalyticsEvent({
                                eventName: 'createAccount',
                                userId: userData.customerId,
                                email: data.email,
                                registered: true,
                                phone: data.mobile || '',
                                includePlainEmail: true,
                                extraData: {
                                    first_name: data.firstName,
                                    last_name: data.lastName,
                                    birthday: data.birthday || '',
                                    ...dlQueryParams
                                }
                            })
                        }
                        setRegistrationSuccess(true)
                    }
                }
            )
        } catch (error) {
            let errorBody
            if (error?.response instanceof Response) {
                try {
                    errorBody = await error.response.clone().json()
                } catch {
                    const text = await error.response.text()
                    try {
                        errorBody = JSON.parse(text)
                    } catch {
                        errorBody = {message: text}
                    }
                }
            }

            if (errorBody?.title === 'Login Already In Use') {
                form.setError('global', {
                    type: 'manual',
                    message: formatMessage({
                        id: 'global.error.api_login_already_in_use',
                        defaultMessage:
                            'Looks like you’re already a member! Sign in and add your phone number for easy look-up in store.'
                    })
                })
            } else if (errorBody?.title === 'Customer Already Registered') {
                form.setError('global', {
                    type: 'manual',
                    message: formatMessage({
                        id: 'global.error.api_login_already_in_use',
                        defaultMessage:
                            'Looks like you’re already a member! Sign in and add your phone number for easy look-up in store.'
                    })
                })
            } else if (errorBody?.title === 'Invalid Password') {
                form.setError('global', {
                    type: 'manual',
                    message: formatMessage({
                        id: 'global.error.api_invalid_password',
                        defaultMessage: "The password doesn't meet the requirements."
                    })
                })
            } else if (errorBody?.statusCode === 'BUS-1003') {
                form.setError('global', {
                    type: 'manual',
                    message: formatMessage({
                        id: 'global.error.recaptha_failed.label',
                        defaultMessage: 'Please verify that you are not a robot.'
                    })
                })
            } else if (errorBody?.statusCode === 'BUS-2003') {
                form.setError('global', {
                    type: 'manual',
                    message: formatMessage({
                        id: 'global.error.required.fields.label',
                        defaultMessage: 'Required details are missing for the user.'
                    })
                })
            } else {
                form.setError('global', {type: 'manual', message: formatMessage(API_ERROR_MESSAGE)})
            }
        }
    }

    useEffect(() => {
        if (isRegistered) {
            navigate('/account')
        }
        return () => {
            setRegistrationSuccess(false)
        }
    }, [isRegistered, registrationSuccess])

    /**************** Einstein ****************/
    useEffect(() => {
        einstein.sendViewPage(pathname)
    }, [])

    useEffect(() => {
        form.setValue('email', email)
    }, [email])

    useEffect(() => {
        if (!search) return

        const storeID = decodeURIComponent(searchParams.get('storeID'))
        const regType = decodeURIComponent(searchParams.get('registration_type'))
        const cashier = decodeURIComponent(searchParams.get('cashier'))
        setDLQueryParams({
            ...(storeID && storeID !== 'null' && {storeID}),
            ...(regType && regType !== 'null' && {registration_type: regType}),
            ...(cashier && cashier !== 'null' && {cashier})
        })
    }, [search])

    return (
        <Box data-testid="registration-page" bg="gray.50" py={[8, 16]}>
            <Seo title="Registration" description="Customer sign up" />
            <Container bg="white" sx={registerModalContent}>
                <RegisterForm
                    submitForm={submitForm}
                    form={form}
                    clickSignIn={() => navigate('/login')}
                />
            </Container>
        </Box>
    )
}

Registration.getTemplateName = () => 'registration'

Registration.propTypes = {
    match: PropTypes.object
}

export default Registration
